// ============================================================================
// BP-09-04: Support Group Types
// BrightPath Community - Support Groups Feature
// ============================================================================

import { z } from 'zod';

// =============================================================================
// Enums
// =============================================================================

export const GroupTypeEnum = z.enum([
  'online',
  'in_person',
  'hybrid',
]);
export type GroupType = z.infer<typeof GroupTypeEnum>;

export const MemberRoleEnum = z.enum([
  'member',
  'facilitator',
  'co_facilitator',
]);
export type MemberRole = z.infer<typeof MemberRoleEnum>;

export const MemberStatusEnum = z.enum([
  'pending',
  'active',
  'removed',
]);
export type MemberStatus = z.infer<typeof MemberStatusEnum>;

// =============================================================================
// Support Group
// =============================================================================

export const GroupDiagnosisSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
});

export type GroupDiagnosis = z.infer<typeof GroupDiagnosisSchema>;

export const GroupFacilitatorSchema = z.object({
  id: z.string().uuid(),
  first_name: z.string(),
  last_name: z.string(),
  avatar_url: z.string().nullable(),
});

export type GroupFacilitator = z.infer<typeof GroupFacilitatorSchema>;

export const SupportGroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  group_type: GroupTypeEnum,
  diagnoses: z.array(GroupDiagnosisSchema),
  meeting_schedule: z.string().nullable(),
  next_meeting_at: z.string().datetime().nullable(),
  meeting_link: z.string().nullable(),
  meeting_location: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  max_members: z.number().nullable(),
  current_member_count: z.number(),
  facilitator: GroupFacilitatorSchema,
  co_facilitators: z.array(GroupFacilitatorSchema),
  is_active: z.boolean(),
  is_private: z.boolean(),
  is_member: z.boolean(),
  member_status: MemberStatusEnum.nullable(),
  member_role: MemberRoleEnum.nullable(),
  created_at: z.string().datetime(),
});

export type SupportGroup = z.infer<typeof SupportGroupSchema>;

export const SupportGroupListItemSchema = SupportGroupSchema.omit({
  description: true,
  meeting_link: true,
}).extend({
  description_excerpt: z.string(),
});

export type SupportGroupListItem = z.infer<typeof SupportGroupListItemSchema>;

// =============================================================================
// Support Group Member
// =============================================================================

export const SupportGroupMemberSchema = z.object({
  id: z.string().uuid(),
  group_id: z.string().uuid(),
  user: GroupFacilitatorSchema,
  role: MemberRoleEnum,
  status: MemberStatusEnum,
  joined_at: z.string().datetime(),
  last_attended_at: z.string().datetime().nullable(),
});

export type SupportGroupMember = z.infer<typeof SupportGroupMemberSchema>;

// =============================================================================
// Support Group Meeting
// =============================================================================

export const SupportGroupMeetingSchema = z.object({
  id: z.string().uuid(),
  group_id: z.string().uuid(),
  title: z.string(),
  starts_at: z.string().datetime(),
  ends_at: z.string().datetime(),
  meeting_link: z.string().nullable(),
  notes: z.string().nullable(),
  attendee_count: z.number(),
  recording_url: z.string().nullable(),
  created_at: z.string().datetime(),
});

export type SupportGroupMeeting = z.infer<typeof SupportGroupMeetingSchema>;

// =============================================================================
// Create/Update Schemas
// =============================================================================

export const CreateSupportGroupSchema = z.object({
  name: z.string().min(5, 'Name must be at least 5 characters').max(200),
  description: z.string().min(20, 'Description must be at least 20 characters').max(2000),
  group_type: GroupTypeEnum,
  diagnosis_ids: z.array(z.string().uuid()).min(1, 'Select at least one diagnosis'),
  meeting_schedule: z.string().max(200).optional(),
  meeting_link: z.string().url('Must be a valid URL').optional(),
  meeting_location: z.string().max(500).optional(),
  city: z.string().max(100).optional(),
  state: z.string().max(50).optional(),
  max_members: z.number().min(2).max(100).optional(),
  is_private: z.boolean().default(false),
});

export type CreateSupportGroupInput = z.infer<typeof CreateSupportGroupSchema>;

export const UpdateSupportGroupSchema = CreateSupportGroupSchema.partial();

export type UpdateSupportGroupInput = z.infer<typeof UpdateSupportGroupSchema>;

export const CreateMeetingSchema = z.object({
  title: z.string().min(5).max(200),
  starts_at: z.string().datetime(),
  ends_at: z.string().datetime(),
  meeting_link: z.string().url().optional(),
  notes: z.string().max(1000).optional(),
});

export type CreateMeetingInput = z.infer<typeof CreateMeetingSchema>;

export const UpdateMemberSchema = z.object({
  role: MemberRoleEnum.optional(),
  status: MemberStatusEnum.optional(),
});

export type UpdateMemberInput = z.infer<typeof UpdateMemberSchema>;

// =============================================================================
// Query Parameters
// =============================================================================

export interface SupportGroupSearchParams {
  diagnosis_id?: string;
  group_type?: GroupType;
  city?: string;
  state?: string;
  has_availability?: boolean;
  page?: number;
  limit?: number;
}

// =============================================================================
// Response Types
// =============================================================================

export interface SupportGroupsResponse {
  groups: SupportGroupListItem[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

export interface SupportGroupDetailResponse {
  group: SupportGroup;
  upcoming_meetings: SupportGroupMeeting[];
  recent_meetings: SupportGroupMeeting[];
}

export interface UserGroupsResponse {
  member_of: SupportGroupListItem[];
  facilitating: SupportGroupListItem[];
}

export interface GroupStatsResponse {
  total_groups: number;
  online_groups: number;
  in_person_groups: number;
  active_members: number;
  upcoming_meetings: number;
}
